// Typed wrappers around `/api/staff/audit/*` (feature F-E Phase 4).
//
// Mirrors shared/src/api/users.ts. Consumed by staff/src/features/audit/.
// Admin-only (enforced server-side via ROLE_PERMS); a non-admin caller gets an
// ApiError with status 403.
//
// Wire-casing note: request query params are snake_case (FastAPI param names,
// like users.ts sends `include_deleted`); response bodies are camelCase
// (ADR-0020) — read `actorId`, `nextCursor`, etc.

import { getApiClient } from "./client";
import type { components } from "./types";

export type AuditEvent = components["schemas"]["AuditEventResponse"];
export type AuditEventPage = components["schemas"]["AuditEventPage"];

export interface AuditFilters {
  actorId?: string;
  action?: string;
  resourceKind?: string;
  resourceId?: string;
  decision?: "allow" | "deny";
  from?: string; // ISO-8601 UTC (frontend converts its HKT pickers to UTC)
  to?: string; // ISO-8601 UTC
}

function toParams(f: AuditFilters): Record<string, string> {
  const p: Record<string, string> = {};
  if (f.actorId) p.actor_id = f.actorId;
  if (f.action) p.action = f.action;
  if (f.resourceKind) p.resource_kind = f.resourceKind;
  if (f.resourceId) p.resource_id = f.resourceId;
  if (f.decision) p.decision = f.decision;
  if (f.from) p.from = f.from;
  if (f.to) p.to = f.to;
  return p;
}

export async function listAudit(
  filters: AuditFilters,
  opts?: { cursor?: string; limit?: number },
): Promise<AuditEventPage> {
  const params: Record<string, string | number> = { ...toParams(filters) };
  if (opts?.cursor) params.cursor = opts.cursor;
  if (opts?.limit) params.limit = opts.limit;
  const { data } = await getApiClient().get<AuditEventPage>("/staff/audit", {
    params,
  });
  return data;
}

export async function getAuditEvent(eventId: string): Promise<AuditEvent> {
  const { data } = await getApiClient().get<AuditEvent>(
    `/staff/audit/${eventId}`,
  );
  return data;
}

export interface AuditCsvResult {
  blob: Blob;
  truncated: boolean;
}

export async function exportAuditCsv(
  filters: AuditFilters,
): Promise<AuditCsvResult> {
  const res = await getApiClient().get<Blob>("/staff/audit/export", {
    params: toParams(filters),
    responseType: "blob",
  });
  const header = res.headers?.["x-audit-export-truncated"];
  return { blob: res.data, truncated: String(header).toLowerCase() === "true" };
}
